import {
  ReportError,
  type AcquisitionCostsReport,
  type ActivityReport,
  type CallHoursReport,
  type CallOutcomesReport,
  type DataQualityReport,
  type DealSourceRow,
  type FunnelReport,
  type LeaderboardReport,
  type LossReasonRow,
  type ReportFilters,
  type ReportsClient,
  type RepConversationsPage,
  type RepStatsReport,
  type LiveSession,
  type SessionDetail,
  type TeamSessionsReport,
} from "../types";

export type RpcClient = {
  rpc: (
    fn: string,
    args: Record<string, unknown>,
  ) => PromiseLike<{
    data: unknown;
    error: { message: string; code?: string } | null;
  }>;
};

export const cleanFilters = (filters: ReportFilters): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

const toReportError = (e: { message: string }) =>
  new ReportError(
    /forbidden/.test(e.message)
      ? "forbidden"
      : /not_found/.test(e.message)
        ? "not_found"
        : /invalid_period/.test(e.message)
          ? "invalid_period"
          : /invalid_filters/.test(e.message)
            ? "invalid_filters"
            : "unknown",
    e.message,
  );

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const arrays = (v: Record<string, unknown>, keys: string[]) =>
  keys.every((k) => Array.isArray(v[k]));

type Guard<T> = (v: unknown) => v is T;
const guard =
  <T>(check: (v: unknown) => boolean): Guard<T> =>
  (v): v is T =>
    check(v);
const isFunnel = guard<FunnelReport>(
  (v) => isObject(v) && arrays(v, ["prospecting", "deals"]),
);
const isActivity = guard<ActivityReport>(
  (v) =>
    isObject(v) &&
    isObject(v.totals) &&
    isObject(v.pipeline) &&
    arrays(v, ["by_rep", "by_rep_day", "by_day"]),
);
const isCallOutcomes = guard<CallOutcomesReport>(
  (v) =>
    isObject(v) &&
    arrays(v, ["by_outcome", "by_trade", "by_region", "heatmap"]),
);
const isDataQuality = guard<DataQualityReport>(
  (v) => isObject(v) && typeof v.total === "number",
);
const isLeaderboard = guard<LeaderboardReport>(
  (v) =>
    isObject(v) &&
    typeof v.period === "string" &&
    typeof v.from === "string" &&
    typeof v.to === "string" &&
    arrays(v, ["rows"]),
);
const isCallHours = guard<CallHoursReport>(
  (v) =>
    isObject(v) &&
    typeof v.days === "number" &&
    typeof v.min_dials === "number" &&
    typeof v.min_sample === "number" &&
    arrays(v, ["cells", "recommendations"]),
);
const isRepStats = guard<RepStatsReport>(
  (v) =>
    isObject(v) &&
    isObject(v.kpis) &&
    isObject(v.previous) &&
    typeof v.from === "string" &&
    arrays(v, [
      "by_day",
      "by_outcome",
      "not_interested",
      "by_trade",
      "by_region",
    ]),
);
const isRepConversations = guard<RepConversationsPage>(
  (v) => isObject(v) && typeof v.total === "number" && arrays(v, ["rows"]),
);
const isAcquisitionCosts = guard<AcquisitionCostsReport>(
  (v) =>
    isObject(v) &&
    typeof v.has_costs === "boolean" &&
    isObject(v.team) &&
    arrays(v, ["reps", "channels"]),
);
// GAVION team sessions
const isTeamSessions = guard<TeamSessionsReport>(
  (v) =>
    isObject(v) &&
    typeof v.total === "number" &&
    isObject(v.totals) &&
    arrays(v, ["rows"]),
);
const isLiveSessions = guard<LiveSession[]>((v) => Array.isArray(v));
const isSessionDetail = guard<SessionDetail>(
  (v) => isObject(v) && isObject(v.session) && arrays(v, ["calls"]),
);
const isRows = <T>(v: unknown): v is T[] => Array.isArray(v);
const isRecordRows = (v: unknown): v is Record<string, unknown>[] =>
  Array.isArray(v) && v.every(isObject);

/** RPC contract with supabase/schemas/24_gavion_funnel.sql, 24_gavion_reports.sql, 30_gavion_goals.sql,
 * 35_gavion_call_hours.sql, 48_gavion_rep_stats.sql and 59_gavion_team_sessions.sql. */
export const createSupabaseReportsClient = (
  getClient: () => RpcClient,
): ReportsClient => {
  const call = async <T>(
    fn: string,
    args: Record<string, unknown>,
    isValid: Guard<T>,
    emptyAs?: T,
  ): Promise<T> => {
    const { data, error } = await getClient().rpc(fn, args);
    if (error) throw toReportError(error);
    if (data == null && emptyAs !== undefined) return emptyAs;
    if (!isValid(data))
      throw new ReportError("unknown", `${fn}: unexpected result shape`);
    return data;
  };
  const period = (from: string, to: string, filters: ReportFilters) => ({
    p_from: from,
    p_to: to,
    p_filters: cleanFilters(filters),
  });
  return {
    funnel: (from, to, filters) =>
      call("funnel_report", period(from, to, filters), isFunnel),
    activity: (from, to, filters) =>
      call("activity_report", period(from, to, filters), isActivity),
    callOutcomes: (from, to, filters) =>
      call("report_call_outcomes", period(from, to, filters), isCallOutcomes),
    lossReasons: (from, to, filters) =>
      call(
        "report_loss_reasons",
        period(from, to, filters),
        isRows<LossReasonRow>,
      ),
    dealSources: (from, to, filters) =>
      call(
        "report_deal_sources",
        period(from, to, filters),
        isRows<DealSourceRow>,
      ),
    dataQuality: () => call("report_data_quality", {}, isDataQuality),
    leaderboard: (period) =>
      call("gavion_leaderboard", { p_period: period }, isLeaderboard),
    callHours: (days, filters) =>
      call(
        "gavion_call_hours",
        { p_days: days, p_filters: cleanFilters(filters) },
        isCallHours,
      ),
    repStats: (salesId, from, to) =>
      call(
        "gavion_rep_stats",
        { p_sales_id: salesId, p_from: from, p_to: to },
        isRepStats,
      ),
    repConversations: (salesId, from, to, query) =>
      call(
        "gavion_rep_conversations",
        {
          p_sales_id: salesId,
          p_from: from,
          p_to: to,
          p_outcome: query.outcome ?? null,
          p_limit: query.limit,
          p_offset: query.offset,
        },
        isRepConversations,
      ),
    acquisitionCosts: (from, to) =>
      call(
        "gavion_acquisition_costs",
        { p_from: from, p_to: to },
        isAcquisitionCosts,
      ),
    // GAVION team sessions (admins only, re-checked in SQL)
    teamSessions: (from, to, query) =>
      call(
        "gavion_team_sessions",
        {
          p_from: from,
          p_to: to,
          p_sales_id: query.salesId,
          p_limit: query.limit,
          p_offset: query.offset,
        },
        isTeamSessions,
      ),
    liveSessions: () => call("gavion_live_sessions", {}, isLiveSessions),
    sessionCalls: (sessionId) =>
      call(
        "gavion_session_calls",
        { p_session_id: sessionId },
        isSessionDetail,
      ),
    exportRows: (table, from, to, filters) =>
      call(
        "export_report",
        { p_table: table, ...period(from, to, filters) },
        isRecordRows,
        [],
      ),
  };
};
